/**
 * StellarLoadingScreen.jsx — premium app splash (2026-06-11)
 *
 * Used by AppRoutes while the auth context is hydrating. Designed to feel
 * calm, luxe, and branded — NOT flashy. CSS keyframes are inlined via a
 * single <style> tag so the component stays self-contained and works on
 * the first paint (no Tailwind purge surprises).
 *
 * Behaviour:
 *   • Background: deep black with a soft, dim warm-gold radial glow
 *     centred behind the logo.
 *   • Logo: fades in then "breathes" forever (subtle scale 1 ↔ 1.04).
 *   • Wordmark "Loading Stellar…" fades in ~600ms after the logo and
 *     uses a thin tracked-out typeface.
 *   • Progress strip: a 1px thin gold shimmer that sweeps left→right.
 *
 * Theme rule: this screen always shows the dark variant — loading begins
 * before the user's theme preference is read.
 */
import React from 'react';

const LOGO_URL = '/stp-logo.png';

export default function StellarLoadingScreen({ label = 'Loading Stellar…' }) {
  return (
    <div className="stellar-splash" role="status" aria-live="polite" data-testid="stellar-splash">
      <style>{`
        .stellar-splash{
          position: fixed; inset: 0;
          display: flex; align-items: center; justify-content: center;
          flex-direction: column; gap: 2.5rem;
          background: #060606;
          color: #f5e7c4;
          z-index: 9999;
          overflow: hidden;
        }
        /* Soft, dim warm-gold radial glow behind the logo. Pure CSS, no JS. */
        .stellar-splash::before{
          content: '';
          position: absolute;
          width: 60vmin; height: 60vmin;
          left: 50%; top: 42%;
          transform: translate(-50%, -50%);
          background: radial-gradient(circle at center,
            rgba(212, 175, 95, 0.22) 0%,
            rgba(212, 175, 95, 0.10) 25%,
            rgba(212, 175, 95, 0.04) 50%,
            transparent 70%);
          filter: blur(40px);
          pointer-events: none;
          animation: stellar-glow-pulse 5.5s ease-in-out infinite;
        }
        /* Subtle film grain — adds depth without being noisy. */
        .stellar-splash::after{
          content: '';
          position: absolute; inset: 0;
          background-image:
            radial-gradient(rgba(255,255,255,0.015) 1px, transparent 1px);
          background-size: 3px 3px;
          opacity: 0.6;
          pointer-events: none;
          mix-blend-mode: overlay;
        }

        .stellar-splash__logo-wrap{
          position: relative;
          width: clamp(160px, 22vmin, 220px);
          height: clamp(160px, 22vmin, 220px);
          display: flex; align-items: center; justify-content: center;
          opacity: 0;
          animation:
            stellar-logo-in 1.2s cubic-bezier(.2,.7,.2,1) 0.05s forwards,
            stellar-logo-breathe 4.5s ease-in-out 1.3s infinite;
          will-change: opacity, transform;
        }
        .stellar-splash__logo{
          width: 100%; height: 100%;
          object-fit: contain;
          filter:
            drop-shadow(0 0 16px rgba(212, 175, 95, 0.35))
            drop-shadow(0 0 40px rgba(212, 175, 95, 0.12));
        }
        /* Soft halo ring tracing the logo edge */
        .stellar-splash__halo{
          position: absolute; inset: -8%;
          border-radius: 50%;
          background: radial-gradient(closest-side,
            transparent 62%,
            rgba(212, 175, 95, 0.18) 70%,
            rgba(212, 175, 95, 0.06) 86%,
            transparent 100%);
          animation: stellar-halo-rotate 18s linear infinite;
          pointer-events: none;
        }

        .stellar-splash__label{
          position: relative;
          font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
          font-size: clamp(15px, 1.6vmin, 18px);
          font-weight: 300;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          color: rgba(245, 231, 196, 0.85);
          opacity: 0;
          transform: translateY(6px);
          animation: stellar-label-in 0.9s cubic-bezier(.2,.7,.2,1) 1.1s forwards;
          text-shadow: 0 0 12px rgba(212, 175, 95, 0.18);
        }
        /* Slow pulse on the dot ellipsis (the last character) */
        .stellar-splash__label::after{
          content: '';
          display: inline-block;
          width: 4px; height: 4px;
          margin-left: 8px;
          border-radius: 50%;
          background: rgba(212, 175, 95, 0.9);
          vertical-align: middle;
          animation: stellar-dot-pulse 1.6s ease-in-out infinite;
        }

        /* Premium progress shimmer — 1px hairline that sweeps. */
        .stellar-splash__progress{
          position: relative;
          width: clamp(180px, 24vmin, 280px);
          height: 1px;
          background: linear-gradient(90deg,
            rgba(212,175,95,0) 0%,
            rgba(212,175,95,0.18) 50%,
            rgba(212,175,95,0) 100%);
          overflow: hidden;
          border-radius: 1px;
          opacity: 0;
          animation: stellar-progress-in 0.9s ease 1.3s forwards;
        }
        .stellar-splash__progress::after{
          content: '';
          position: absolute; top: 0; left: -40%;
          width: 40%; height: 100%;
          background: linear-gradient(90deg,
            rgba(212,175,95,0) 0%,
            rgba(212,175,95,0.6) 50%,
            rgba(212,175,95,0.95) 70%,
            rgba(212,175,95,0) 100%);
          filter: blur(0.5px);
          animation: stellar-shimmer 2.2s cubic-bezier(.4,0,.2,1) infinite;
        }

        /* ── Keyframes ─────────────────────────────────────────────── */
        @keyframes stellar-logo-in {
          0%   { opacity: 0; transform: scale(0.94); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes stellar-logo-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.045); }
        }
        @keyframes stellar-glow-pulse {
          0%, 100% { opacity: 0.85; transform: translate(-50%, -50%) scale(1); }
          50%      { opacity: 1;    transform: translate(-50%, -50%) scale(1.12); }
        }
        @keyframes stellar-halo-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes stellar-label-in {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes stellar-dot-pulse {
          0%, 100% { opacity: 0.35; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1.25); }
        }
        @keyframes stellar-progress-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes stellar-shimmer {
          0%   { left: -40%; }
          100% { left: 120%; }
        }
        /* Reduced-motion accessibility — stop the breathing for users
           who set prefers-reduced-motion. The glow stays still too. */
        @media (prefers-reduced-motion: reduce) {
          .stellar-splash__logo-wrap{ animation: stellar-logo-in 0.4s ease forwards; }
          .stellar-splash::before{ animation: none; }
          .stellar-splash__progress::after{ animation: none; opacity: 0.4; left: 30%; }
          .stellar-splash__label::after{ animation: none; }
        }
      `}</style>

      <div className="stellar-splash__logo-wrap">
        <span className="stellar-splash__halo" aria-hidden="true" />
        <img
          src={LOGO_URL}
          alt="Stellar Productions"
          className="stellar-splash__logo"
          draggable={false}
        />
      </div>

      <div className="stellar-splash__progress" aria-hidden="true" />

      <div className="stellar-splash__label">{label}</div>
    </div>
  );
}
